/* ==========================================================================
   Custom Fields Editor
   Admin component for defining custom fields on catalog products
   ========================================================================== */

import { useState } from 'react'
import { Input } from '@/components/ui/Input'
import { Select, type SelectOption } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { FormField } from '@/components/ui/FormField'
import { ProviderMultiSelect } from '@/components/admin/ProviderMultiSelect'
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import type { CustomFieldDefinition, ProviderResponse } from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface CustomFieldsEditorProps {
  value: CustomFieldDefinition[]
  onChange: (fields: CustomFieldDefinition[]) => void
  disabled?: boolean
  /** Section label; defaults to "Custom Fields". */
  label?: string
  /** Copy shown when the list is empty. */
  emptyMessage?: string
  /**
   * PORTAL-181: when given, each field gets a "Visible to" assignment
   * (general custom fields only — product fields never pass this, so their
   * payloads never carry `provider_ids`). Hosts pass it only once the provider
   * list has loaded, so unknown ids really are removed providers (only
   * possible if another admin deleted one while this page was open — the
   * backend strips deleted providers from every assignment).
   */
  providers?: ProviderResponse[]
}

/**
 * PORTAL-181: the three states of `provider_ids` on a general field.
 *   null / absent → every provider   ("all")
 *   non-empty list → those providers ("selected")
 *   []             → no one          ("none": parked, or last assignee deleted)
 */
type Visibility = 'all' | 'selected' | 'none'

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const FIELD_TYPE_OPTIONS: SelectOption[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
]

const VISIBILITY_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All providers' },
  { value: 'selected', label: 'Selected providers' },
  { value: 'none', label: 'No one' },
]

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/^[^a-z]/, '')
    .replace(/_+/g, '_')
    .slice(0, 50)
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function CustomFieldsEditor({
  value,
  onChange,
  disabled = false,
  label = 'Custom Fields',
  emptyMessage = 'No custom fields defined. Add fields to collect additional information from providers.',
  providers,
}: CustomFieldsEditorProps) {
  const [expandedIndex, setExpandedIndexState] = useState<number | null>(null)
  // PORTAL-181: index of the field whose admin chose "Selected providers" but
  // has not picked anyone yet. Its provider_ids is [] (what would be saved),
  // yet the select must keep reading "Selected providers" instead of jumping
  // to "No one" — and jumping back the moment the last chip is removed.
  const [pickingProvidersFor, setPickingProvidersFor] = useState<number | null>(null)

  function setExpandedIndex(index: number | null) {
    setExpandedIndexState(index)
    setPickingProvidersFor(null)
  }

  function addField() {
    if (value.length >= 20) return
    const newField: CustomFieldDefinition = {
      key: '',
      label: '',
      type: 'text',
      required: false,
      options: null,
      placeholder: null,
      helper_text: null,
    }
    onChange([...value, newField])
    setExpandedIndex(value.length)
  }

  function removeField(index: number) {
    const updated = value.filter((_, i) => i !== index)
    onChange(updated)
    if (expandedIndex === index) {
      setExpandedIndex(null)
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1)
    }
  }

  function visibilityOf(field: CustomFieldDefinition, index: number): Visibility {
    const ids = field.provider_ids
    if (ids === null || ids === undefined) return 'all'
    if (ids.length > 0 || pickingProvidersFor === index) return 'selected'
    return 'none'
  }

  function setVisibility(index: number, mode: Visibility) {
    if (mode === 'all') {
      updateField(index, { provider_ids: null })
      setPickingProvidersFor(null)
    } else if (mode === 'none') {
      updateField(index, { provider_ids: [] })
      setPickingProvidersFor(null)
    } else {
      const current = value[index].provider_ids ?? []
      updateField(index, { provider_ids: current })
      setPickingProvidersFor(current.length === 0 ? index : null)
    }
  }

  function updateField(index: number, updates: Partial<CustomFieldDefinition>) {
    const updated = value.map((field, i) => {
      if (i !== index) return field
      const merged = { ...field, ...updates }

      // Auto-generate key from label
      if ('label' in updates && updates.label !== undefined) {
        merged.key = slugify(updates.label)
      }

      // Clear options if switching away from dropdown
      if ('type' in updates && updates.type !== 'dropdown') {
        merged.options = null
      }

      // Initialize options if switching to dropdown
      if ('type' in updates && updates.type === 'dropdown' && !merged.options) {
        merged.options = ['']
      }

      return merged
    })
    onChange(updated)
  }

  function updateOption(fieldIndex: number, optionIndex: number, newValue: string) {
    const field = value[fieldIndex]
    if (!field.options) return
    const newOptions = [...field.options]
    newOptions[optionIndex] = newValue
    updateField(fieldIndex, { options: newOptions })
  }

  function addOption(fieldIndex: number) {
    const field = value[fieldIndex]
    const options = field.options ? [...field.options, ''] : ['']
    updateField(fieldIndex, { options })
  }

  function removeOption(fieldIndex: number, optionIndex: number) {
    const field = value[fieldIndex]
    if (!field.options || field.options.length <= 1) return
    const options = field.options.filter((_, i) => i !== optionIndex)
    updateField(fieldIndex, { options })
  }

  // Check for duplicate keys
  const keys = value.map((f) => f.key).filter(Boolean)
  const hasDuplicateKeys = keys.length !== new Set(keys).size

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[var(--color-charcoal)]">
          {label}
        </label>
        <span className="text-xs text-[var(--color-gray-500)]">
          {value.length}/20 fields
        </span>
      </div>

      {hasDuplicateKeys && (
        <p className="text-sm text-[var(--color-error)]">
          Duplicate field keys detected. Each field must have a unique key.
        </p>
      )}

      {value.length === 0 && (
        <p className="text-sm text-[var(--color-gray-500)]">{emptyMessage}</p>
      )}

      {value.map((field, index) => {
        const isExpanded = expandedIndex === index
        const hasError = !field.label.trim() || !field.key
        const isDropdownMissingOptions =
          field.type === 'dropdown' &&
          (!field.options || field.options.length === 0 || field.options.every((o) => !o.trim()))
        // PORTAL-181: ids no longer matching a provider (deleted by another
        // admin since this page loaded)
        const providerIds = field.provider_ids ?? []
        const visibility = visibilityOf(field, index)
        const removedProviderCount =
          providers !== undefined
            ? providerIds.filter((id) => !providers.some((p) => p.id === id)).length
            : 0
        const hasRemovedProvider = removedProviderCount > 0
        const visibilityBadge =
          visibility === 'all'
            ? 'All providers'
            : providerIds.length === 0
              ? 'No one'
              : `${providerIds.length} provider${providerIds.length === 1 ? '' : 's'}${
                  hasRemovedProvider ? ` (${removedProviderCount} removed)` : ''
                }`

        return (
          <div
            key={index}
            className={`border rounded-lg ${hasError || isDropdownMissingOptions || hasRemovedProvider ? 'border-[var(--color-error)]' : 'border-[var(--color-gray-200)]'}`}
          >
            {/* Field header row */}
            <div
              className="flex items-center justify-between p-3 cursor-pointer"
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-mono text-[var(--color-gray-500)] bg-[var(--color-gray-100)] px-1.5 py-0.5 rounded">
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-[var(--color-charcoal)] truncate">
                  {field.label || '(untitled field)'}
                </span>
                <span className="text-xs text-[var(--color-gray-500)]">
                  {field.type}
                </span>
                {field.required && (
                  <span className="text-xs text-[var(--color-error)]">required</span>
                )}
                {providers !== undefined && (
                  <span
                    className={`text-xs whitespace-nowrap ${
                      visibility === 'none'
                        ? 'text-[var(--color-warning)]'
                        : 'text-[var(--color-gray-500)]'
                    }`}
                  >
                    {visibilityBadge}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeField(index)
                  }}
                  disabled={disabled}
                  className="text-[var(--color-error)] hover:text-[var(--color-error)]"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-[var(--color-gray-500)]" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-[var(--color-gray-500)]" />
                )}
              </div>
            </div>

            {/* Expanded editor */}
            {isExpanded && (
              <div className="p-3 pt-0 space-y-3 border-t border-[var(--color-gray-200)]">
                {/* Label and Type row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <FormField label="Label" required htmlFor={`cf-label-${index}`}>
                    <Input
                      id={`cf-label-${index}`}
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      disabled={disabled}
                      placeholder="e.g., Injection Site"
                      error={!field.label.trim()}
                    />
                  </FormField>

                  <FormField label="Type" required htmlFor={`cf-type-${index}`}>
                    <Select
                      options={FIELD_TYPE_OPTIONS}
                      value={field.type}
                      onChange={(v) =>
                        updateField(index, {
                          type: v as CustomFieldDefinition['type'],
                        })
                      }
                      disabled={disabled}
                    />
                  </FormField>
                </div>

                {/* Auto-generated key display */}
                {field.key && (
                  <p className="text-xs text-[var(--color-gray-500)]">
                    Key: <code className="font-mono bg-[var(--color-gray-100)] px-1 rounded">{field.key}</code>
                  </p>
                )}

                {/* Required toggle */}
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => updateField(index, { required: e.target.checked })}
                    disabled={disabled}
                    className="rounded border-[var(--color-gray-300)] text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-primary)]"
                  />
                  Required field
                </label>

                {/* Provider assignment (PORTAL-181, general fields only) */}
                {providers !== undefined && (
                  <div className="space-y-3">
                    <FormField
                      label="Visible to"
                      helperText={
                        visibility === 'none'
                          ? 'This field is not asked of anyone. Pick "All providers" or assign providers to show it again.'
                          : undefined
                      }
                    >
                      <Select
                        options={VISIBILITY_OPTIONS}
                        value={visibility}
                        onChange={(v) => setVisibility(index, v as Visibility)}
                        disabled={disabled}
                      />
                    </FormField>

                    {visibility === 'selected' && (
                      <FormField
                        label="Providers"
                        htmlFor={`cf-providers-${index}`}
                        helperText={
                          providerIds.length === 0
                            ? 'No one is asked until you pick at least one provider.'
                            : undefined
                        }
                      >
                        <ProviderMultiSelect
                          providers={providers}
                          selectedIds={providerIds}
                          onChange={(ids) => {
                            updateField(index, { provider_ids: ids })
                            // Removing the last chip must not flip the select to "No one".
                            setPickingProvidersFor(ids.length === 0 ? index : null)
                          }}
                          inputId={`cf-providers-${index}`}
                          disabled={disabled}
                        />
                      </FormField>
                    )}
                  </div>
                )}

                {/* Dropdown options editor */}
                {field.type === 'dropdown' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-[var(--color-charcoal)]">
                      Options
                    </label>
                    {(field.options || []).map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={option}
                          onChange={(e) => updateOption(index, optIndex, e.target.value)}
                          disabled={disabled}
                          placeholder={`Option ${optIndex + 1}`}
                          error={!option.trim()}
                        />
                        {(field.options?.length || 0) > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOption(index, optIndex)}
                            disabled={disabled}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => addOption(index)}
                      disabled={disabled}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add option
                    </Button>
                  </div>
                )}

                {/* Optional: Placeholder and Helper text */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label="Placeholder" htmlFor={`cf-placeholder-${index}`}>
                    <Input
                      id={`cf-placeholder-${index}`}
                      type="text"
                      value={field.placeholder || ''}
                      onChange={(e) =>
                        updateField(index, {
                          placeholder: e.target.value || null,
                        })
                      }
                      disabled={disabled}
                      placeholder="Optional placeholder text"
                    />
                  </FormField>

                  <FormField label="Helper Text" htmlFor={`cf-helper-${index}`}>
                    <Input
                      id={`cf-helper-${index}`}
                      type="text"
                      value={field.helper_text || ''}
                      onChange={(e) =>
                        updateField(index, {
                          helper_text: e.target.value || null,
                        })
                      }
                      disabled={disabled}
                      placeholder="Optional help message"
                    />
                  </FormField>
                </div>
              </div>
            )}
          </div>
        )
      })}

      {value.length < 20 && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addField}
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" /> Add Custom Field
        </Button>
      )}
    </div>
  )
}

export default CustomFieldsEditor
